import React from 'react';
import { ScoreCapInfo } from './verdict';

interface ScoreExplainerProps {
  cap: ScoreCapInfo | null;
}

/**
 * Hero-zone scoring disclosure: surfaces the severity hard-cap when it bound
 * the score, and an expandable methodology note explaining how the 0-100
 * score and letter grade are computed (issue #360).
 */
export default function ScoreExplainer({ cap }: ScoreExplainerProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <div className="score-explainer">
      {cap && (
        <p className="score-explainer__cap-note">
          {cap.severity === 'warning'
            ? `Score capped at ${cap.cap} because one or more findings could not be fully assessed and are shown as warnings`
            : `Score capped at ${cap.cap} because a ${cap.severity}-severity requirement failed`}
          {cap.uncappedScore !== null ? ` (uncapped checks alone would score ${cap.uncappedScore})` : ''}.
        </p>
      )}
      <button
        type="button"
        className="score-explainer__toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        How is this scored?
      </button>
      {open && (
        <ul className="score-explainer__panel">
          <li>
            Each check carries a point value. The score is points earned out of total points across all tested
            checks; a warning earns half credit. Judgment-based failures carry no point value of their own. They
            are reported but do not otherwise change the number.
          </li>
          <li>
            Severity caps: a failed critical check caps the score at 49 (an F); failed high, medium, and low
            severity checks cap it at 74, 89, and 94 respectively, no matter how many other checks pass, so the
            score never reads 100 while a judgment-based failure is showing. A finding that could not be fully
            assessed (shown as a warning rather than a pass or fail) caps the score at 99 the same way.
          </li>
          <li>Letter grades: A 90-100, B 80-89, C 70-79, D 60-69, F below 60.</li>
          <li>
            Sections shown as N/A were not tested in this scan (lite scans skip some stages) and are excluded
            from the score entirely.
          </li>
        </ul>
      )}
    </div>
  );
}
